import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import {
  Address,
  IAktionen,
  IDifferentInvoiceRecipient,
  IERechnungsInfo,
  IOrderDetailsInfo,
  IProduktSelektion,
  ITariffChangeTrackingData,
  IVertragskonto,
  PredefinedTariffSelectionData,
  PreisIndikation,
  PriceSheet,
  Product,
  TariffChangeAvailabilities,
} from '@mwe/models';
import { ServiceStateEnum } from '@mwe/constants';
import { computed } from '@angular/core';
import { withStorageServiceFeature } from '../../store-features/storage-service.feature';
import { withConfirmationStatusState, ConfirmationStatusState } from '../../store-features/confirmation-status-state.feature';
import { MemoryCacheItem } from '../../cache/memory-cache.service';
import { isProductCategoryFernwaerme, isProductCategoryStromOrGas } from '@mwe/utils';

const STORAGE_KEY = 'tariff-change';

export type TariffChangeStoreType = InstanceType<typeof TariffChangeStore>;

const initialState = {
  directAddressSelection: undefined as boolean,
  isAddressSelectionVisible: undefined as boolean,
  tariffChangeAddress: undefined as Address,
  tariffChangeProducts: undefined as Product[],
  tariffChangeAvailability: undefined as TariffChangeAvailabilities,
  productsForSelection: undefined as Product[],
  preselectedProduct: undefined as Product,
  selectedTariffs: undefined as IProduktSelektion,
  predefinedTariffs: undefined as PredefinedTariffSelectionData[],
  orderDetails: undefined as IOrderDetailsInfo[],
  orderDetailsAddress: undefined as Address, // The address for which the order details were loaded; used to determine if order details need to be reloaded when the user changes address
  eRechnungsInfo: undefined as IERechnungsInfo,
  invoiceData: undefined as IVertragskonto,
  differentInvoiceRecipient: undefined as IDifferentInvoiceRecipient,
  voucherInfo: undefined as IAktionen,
  predefinedVoucherCode: undefined as string,
  immediateDelivery: undefined as boolean,
  customerDataChangeData: undefined as { email: string; customerNumbers: string[] },
  trackingData: undefined as ITariffChangeTrackingData,
  selectedPriceIndications: undefined as PreisIndikation[],
  priceSheets: undefined as PriceSheet[],
  userSelectedOffers: undefined as string[],
  hasUserSelectedOffer: undefined as boolean,
  isSelectedTariffSpot: undefined as boolean,
  tariffChangeProductsState: undefined as ServiceStateEnum,
  tariffSelectionState: undefined as ServiceStateEnum,
  submitSuccessState: undefined as ServiceStateEnum,
  tempProductSelectionForVoucher: undefined as IProduktSelektion,
};

type InitialTariffChangeState = typeof initialState;

export const TariffChangeStore = signalStore(
  { providedIn: 'root' },
  withConfirmationStatusState({
    _memoryCacheItem: MemoryCacheItem.ClearingAccountDetails,
  }),
  withStorageServiceFeature(STORAGE_KEY, true),
  withState(initialState),
  withComputed(store => {
    const stromOrGasOrderDetails = computed(() => (store.orderDetails() ?? []).filter(e => isProductCategoryStromOrGas(e.category)));
    const fernwaermeOrderDetails = computed(() => (store.orderDetails() ?? []).filter(e => isProductCategoryFernwaerme(e.category)));
    const hasOrderDetails = computed(() => (store.orderDetails()?.length ?? 0) > 0);
    return {
      stromOrGasOrderDetails,
      fernwaermeOrderDetails,
      hasOrderDetails,
    };
  }),
  withMethods(store => {
    const updateStoreState = (storeStateValues: Partial<InitialTariffChangeState> & Partial<ConfirmationStatusState>) => {
      patchState(store, { ...storeStateValues });
    };
    /**
     * Reset all per-address derived state when the user picks a different address.
     * Mirrors legacy `TariffChangeLogic#clearStorageDataAfterAddressChange`.
     */
    const clearAfterAddressChange = () => {
      patchState(store, {
        differentInvoiceRecipient: undefined,
        tariffChangeAvailability: undefined,
        productsForSelection: undefined,
        selectedTariffs: undefined,
        orderDetails: undefined,
        orderDetailsAddress: undefined,
        invoiceData: undefined,
        eRechnungsInfo: undefined,
        voucherInfo: undefined,
        immediateDelivery: undefined,
        customerDataChangeData: undefined,
        selectedPriceIndications: undefined,
        userSelectedOffers: undefined,
        hasUserSelectedOffer: undefined,
        priceSheets: undefined,
      });
    };
    /**
     * Reset all process-level state without wiping confirmation status
     * (lastOrderID). Used by the cleanup hooks so the status page can
     * still read lastOrderID from the store after process end.
     *
     * Resets every process field individually via patchState instead of
     * calling store.clear(), so that withWebStorageSync can sync each
     * field to session storage without an intermediate clearStorage()
     * that would wipe the lastOrderID session key before it can be
     * re-persisted.
     */
    const clearProcessState = () => {
      patchState(store, {
        directAddressSelection: undefined,
        isAddressSelectionVisible: undefined,
        tariffChangeAddress: undefined,
        tariffChangeProducts: undefined,
        tariffChangeAvailability: undefined,
        productsForSelection: undefined,
        preselectedProduct: undefined,
        selectedTariffs: undefined,
        predefinedTariffs: undefined,
        orderDetails: undefined,
        eRechnungsInfo: undefined,
        invoiceData: undefined,
        differentInvoiceRecipient: undefined,
        voucherInfo: undefined,
        predefinedVoucherCode: undefined,
        immediateDelivery: undefined,
        customerDataChangeData: undefined,
        trackingData: undefined,
        selectedPriceIndications: undefined,
        priceSheets: undefined,
        userSelectedOffers: undefined,
        hasUserSelectedOffer: undefined,
        isSelectedTariffSpot: undefined,
        tariffChangeProductsState: undefined,
        tariffSelectionState: undefined,
        submitSuccessState: undefined,
        tempProductSelectionForVoucher: undefined,
        submitId: undefined,
      });
    };
    return {
      updateStoreState,
      clearAfterAddressChange,
      clearProcessState,
    };
  }),
);
